import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CONTACT_GROUPS, GENDERS, canEditData, formatDate } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Loader2, Pencil, Search, Trash2, Upload, Download, FileText, FileSpreadsheet, CheckCircle2, Eye } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const AUTO_LAYANAN_TABS = ["layanan_mlu", "khitan", "pinjam_alat", "kartu_sehat"];

const emptyForm = (type) => ({
  type,
  name: "",
  desa: "",
  rt: "",
  rw: "",
  kecamatan: "Dawe",
  phone: "",
  gender: "",
  birth_date: "",
  kode_koin: "",
  custom_id: "",
});

export default function DatabasePage() {
  const { user } = useAuth();
  const [group, setGroup] = useState("donatur");
  const [tab, setTab] = useState("munfiq");
  const fileRef = useRef(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm("munfiq"));
  const [selected, setSelected] = useState([]);
  const [confirmBulk, setConfirmBulk] = useState(false);

  const activeGroup = CONTACT_GROUPS.find((g) => g.key === group);
  const activeType = activeGroup.types.find((t) => t.key === tab);
  const colCount = 5 + (activeType.person ? 2 : 0) + (tab === "koin_nu" ? 1 : 0) + (user?.role === "admin" ? 1 : 0);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: tab };
      if (search) params.search = search;
      const { data } = await api.get("/contacts", { params });
      setContacts(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab, search]);

  useEffect(() => {
    const t = setTimeout(fetchContacts, 250);
    return () => clearTimeout(t);
  }, [fetchContacts]);

  const handleTabChange = (v) => {
    setTab(v);
    setEditingId(null);
    setForm(emptyForm(v));
  };

  const handleGroupChange = (g) => {
    const grp = CONTACT_GROUPS.find((x) => x.key === g);
    setGroup(g);
    setTab(grp.types[0].key);
    setEditingId(null);
    setForm(emptyForm(grp.types[0].key));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) {
      toast.error("Nama wajib diisi");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/contacts/${editingId}`, form);
        toast.success("Kontak diperbarui");
      } else {
        await api.post("/contacts", form);
        toast.success("Kontak ditambahkan");
      }
      setEditingId(null);
      setForm(emptyForm(tab));
      fetchContacts();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (c) => {
    setEditingId(c.contact_id);
    setForm({
      type: c.type,
      name: c.name,
      desa: c.desa || "",
      rt: c.rt || "",
      rw: c.rw || "",
      kecamatan: c.kecamatan || "",
      phone: c.phone || "",
      gender: c.gender || "",
      birth_date: c.birth_date || "",
      kode_koin: c.kode_koin || "",
      custom_id: c.custom_id || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (c) => {
    if (confirmDelete !== c.contact_id) {
      setConfirmDelete(c.contact_id);
      setTimeout(() => setConfirmDelete(null), 3000);
      return;
    }
    try {
      await api.delete(`/contacts/${c.contact_id}`);
      toast.success("Kontak dihapus");
      setConfirmDelete(null);
      fetchContacts();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSelect = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const deletableContacts = contacts.filter((c) => !c.source?.startsWith("form_"));
  const toggleSelectAll = () => setSelected((prev) => (prev.length === deletableContacts.length ? [] : deletableContacts.map((c) => c.contact_id)));
  const handleBulkDelete = async () => {
    if (!confirmBulk) {
      setConfirmBulk(true);
      setTimeout(() => setConfirmBulk(false), 3000);
      return;
    }
    try {
      const { data } = await api.post("/contacts/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} kontak dihapus`);
      setSelected([]);
      setConfirmBulk(false);
      fetchContacts();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [kartuEdit, setKartuEdit] = useState(null);
  const [kartuForm, setKartuForm] = useState({ rm: "", valid_until: "" });
  const [kartuSaving, setKartuSaving] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(null);

  const openKartuEdit = (c) => {
    setKartuForm({ rm: c.rm || "", valid_until: c.valid_until || "" });
    setKartuEdit(c);
  };

  const submitKartuEdit = async (e) => {
    e.preventDefault();
    setKartuSaving(true);
    try {
      await api.put(`/kartu-sehat/${kartuEdit.contact_id.replace("ks_", "")}`, kartuForm);
      toast.success("Data kartu diperbarui");
      setKartuEdit(null);
      fetchContacts();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setKartuSaving(false);
    }
  };

  const handleApproveKartu = async (c) => {
    if (confirmApprove !== c.contact_id) {
      setConfirmApprove(c.contact_id);
      setTimeout(() => setConfirmApprove(null), 3000);
      return;
    }
    try {
      const { data } = await api.post(`/kartu-sehat/${c.contact_id.replace("ks_", "")}/approve`);
      toast.success(data.wa?.ok ? "Kartu disetujui & terkirim ke WA pemohon" : "Kartu disetujui (WA tidak terkirim)");
      setConfirmApprove(null);
      fetchContacts();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleExport = async (format) => {
    try {
      const res = await api.get(tab === "kartu_sehat" ? "/kartu-sehat/export" : "/contacts/export", { params: tab === "kartu_sehat" ? { format } : { format, type: tab }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lazisnu-database-${tab}.${format === "xlsx" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Data berhasil diunduh (${format.toUpperCase()})`);
    } catch (e) {
      toast.error("Gagal mengunduh data");
    }
  };

  const handleTemplate = async () => {
    try {
      const res = await api.get("/contacts/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "contoh-impor-kontak.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Gagal mengunduh contoh file");
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/contacts/import", fd);
      if (data.failed > 0) {
        toast.warning(`Impor: ${data.imported} berhasil, ${data.failed} gagal. ${data.errors?.[0] || ""}`);
      } else {
        toast.success(`${data.imported} kontak berhasil diimpor`);
      }
      fetchContacts();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="database-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-slate-800">Database</h1>
      </div>

      <div className="space-y-3">
        <Tabs value={group} onValueChange={handleGroupChange}>
          <TabsList data-testid="database-group-tabs">
            {CONTACT_GROUPS.map((g) => (
              <TabsTrigger key={g.key} value={g.key} data-testid={`database-group-${g.key}`}>
                {g.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList className="flex-wrap h-auto" data-testid="database-tabs">
            {activeGroup.types.map((t) => (
              <TabsTrigger key={t.key} value={t.key} data-testid={`database-tab-${t.key}`}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {AUTO_LAYANAN_TABS.includes(tab) ? (
          <Card className="lg:col-span-1 h-fit">
            <CardContent className="p-6 text-sm text-slate-500" data-testid="database-auto-note">
              Data Penerima Manfaat <b>{activeType.label}</b> diambil otomatis dari isian Form Permohonan Layanan (menu Permohonan Layanan).
            </CardContent>
          </Card>
        ) : (
        <Card className="lg:col-span-1 h-fit">
          <CardHeader>
            <CardTitle className="text-lg">{editingId ? "Ubah" : "Tambah"} {activeType.label}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <div className="space-y-2">
                <Label>{group === "donatur" ? "ID Donatur" : "ID Penerima Manfaat"}</Label>
                <Input
                  data-testid="contact-custom-id-input"
                  placeholder="Kosongkan untuk generate otomatis"
                  value={form.custom_id}
                  onChange={(e) => setForm({ ...form, custom_id: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Nama {activeType.label}</Label>
                <Input
                  data-testid="contact-name-input"
                  placeholder={`Nama ${activeType.label}`}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Desa</Label>
                  <Input data-testid="contact-desa-input" value={form.desa} onChange={(e) => setForm({ ...form, desa: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Kecamatan</Label>
                  <Input data-testid="contact-kecamatan-input" value={form.kecamatan} onChange={(e) => setForm({ ...form, kecamatan: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>RT</Label>
                  <Input data-testid="contact-rt-input" value={form.rt} onChange={(e) => setForm({ ...form, rt: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>RW</Label>
                  <Input data-testid="contact-rw-input" value={form.rw} onChange={(e) => setForm({ ...form, rw: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>No. WA</Label>
                <Input
                  data-testid="contact-phone-input"
                  placeholder="08xxxxxxxxxx"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>
              {tab === "koin_nu" && (
                <div className="space-y-2">
                  <Label>Kode KOIN</Label>
                  <Input
                    data-testid="contact-kode-koin-input"
                    placeholder="cth: KOIN-001"
                    value={form.kode_koin}
                    onChange={(e) => setForm({ ...form, kode_koin: e.target.value })}
                  />
                </div>
              )}
              {activeType.person && (
                <>
                  <div className="space-y-2">
                    <Label>Jenis Kelamin</Label>
                    <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                      <SelectTrigger data-testid="contact-gender-select">
                        <SelectValue placeholder="Pilih jenis kelamin" />
                      </SelectTrigger>
                      <SelectContent>
                        {GENDERS.map((g) => (
                          <SelectItem key={g.key} value={g.key} data-testid={`contact-gender-${g.key}`}>{g.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tanggal Lahir</Label>
                    <Input
                      type="date"
                      data-testid="contact-birthdate-input"
                      value={form.birth_date}
                      onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                    />
                  </div>
                </>
              )}
              <div className="flex gap-2">
                <Button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={saving}
                  className="flex-1 bg-emerald-800 hover:bg-emerald-700 text-white"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingId ? "Simpan Perubahan" : "Simpan"}
                </Button>
                {editingId && (
                  <Button type="button" variant="outline" data-testid="contact-cancel-edit-button" onClick={() => { setEditingId(null); setForm(emptyForm(tab)); }}>
                    Batal
                  </Button>
                )}
              </div>
            </form>
          </CardContent>
        </Card>
        )}

        <Card className={AUTO_LAYANAN_TABS.includes(tab) ? "lg:col-span-3" : "lg:col-span-2"}>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-lg flex-1">Data {activeType.label} ({contacts.length})</CardTitle>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImportFile} data-testid="contact-import-file-input" />
              <Button type="button" variant="outline" size="sm" data-testid="contact-template-button" onClick={handleTemplate}>
                <Download className="mr-1.5 h-3.5 w-3.5" />Contoh Form
              </Button>
              <Button type="button" variant="outline" size="sm" data-testid="contact-import-button" onClick={() => fileRef.current?.click()}>
                <Upload className="mr-1.5 h-3.5 w-3.5" />Impor Excel
              </Button>
              {user?.role === "admin" && selected.length > 0 && (
                <Button type="button" size="sm" variant={confirmBulk ? "destructive" : "outline"} data-testid="contact-bulk-delete-button" onClick={handleBulkDelete}>
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />{confirmBulk ? `Yakin? Hapus ${selected.length} Data` : `Hapus Terpilih (${selected.length})`}
                </Button>
              )}
              <Button type="button" variant="outline" size="sm" data-testid="contact-export-pdf-button" onClick={() => handleExport("pdf")}>
                <FileText className="mr-1.5 h-3.5 w-3.5 text-red-600" />PDF
              </Button>
              <Button type="button" variant="outline" size="sm" data-testid="contact-export-excel-button" onClick={() => handleExport("xlsx")}>
                <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5 text-emerald-700" />Excel
              </Button>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="contact-search-input"
                  placeholder="Cari nama/desa..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 w-[200px]"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[560px] overflow-auto">
              <Table data-testid="contacts-table">
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow>
                    {user?.role === "admin" && (
                      <TableHead className="w-8">
                        <input type="checkbox" data-testid="contact-select-all" className="h-4 w-4 accent-emerald-700 cursor-pointer" checked={deletableContacts.length > 0 && selected.length === deletableContacts.length} onChange={toggleSelectAll} />
                      </TableHead>
                    )}
                    <TableHead>ID</TableHead>
                    <TableHead>Nama</TableHead>
                    {tab === "koin_nu" && <TableHead>Kode KOIN</TableHead>}
                    <TableHead>Alamat</TableHead>
                    <TableHead>No. WA</TableHead>
                    {activeType.person && <TableHead>L/P</TableHead>}
                    {activeType.person && <TableHead>Tgl Lahir</TableHead>}
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={colCount} className="text-center py-10 text-slate-400">
                        <Loader2 className="h-5 w-5 animate-spin inline-block" />
                      </TableCell>
                    </TableRow>
                  ) : contacts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={colCount} className="text-center py-10 text-slate-400" data-testid="contacts-empty">
                        Belum ada data {activeType.label}
                      </TableCell>
                    </TableRow>
                  ) : (
                    contacts.map((c) => (
                      <TableRow key={c.contact_id} data-testid={`contact-row-${c.contact_id}`}>
                        {user?.role === "admin" && (
                          <TableCell>
                            {!c.source?.startsWith("form_") && (
                              <input type="checkbox" data-testid={`contact-select-${c.contact_id}`} className="h-4 w-4 accent-emerald-700 cursor-pointer" checked={selected.includes(c.contact_id)} onChange={() => toggleSelect(c.contact_id)} />
                            )}
                          </TableCell>
                        )}
                        <TableCell className="font-mono text-xs text-slate-500 whitespace-nowrap" data-testid={`contact-id-${c.contact_id}`}>{c.custom_id || "-"}</TableCell>
                        <TableCell className="font-medium text-slate-800">
                          {c.name}
                          {c.source?.startsWith("form_") && (
                            <span className="ml-2 inline-block rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800">
                              {c.source === "form_khitan" ? "Form Khitan" : c.source === "form_pinjam_alat" ? "Form Pinjam Alat" : c.source === "form_kartu" ? "Form Kartu Sehat" : "Form Layanan"}{c.jadwal ? ` · ${formatDate(c.jadwal)}` : ""}
                            </span>
                          )}
                          {tab === "kartu_sehat" && (
                            <span className={`ml-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${c.kartu_status === "disetujui" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`kartu-status-${c.contact_id}`}>
                              RM: {c.rm || "—"} · {c.kartu_status === "disetujui" ? `Disetujui s.d. ${formatDate(c.valid_until)}` : "Diajukan"}
                            </span>
                          )}
                        </TableCell>
                        {tab === "koin_nu" && <TableCell className="font-mono text-sm">{c.kode_koin || "-"}</TableCell>}
                        <TableCell className="text-slate-500 text-sm">{c.address}</TableCell>
                        <TableCell className="whitespace-nowrap">{c.phone || "-"}</TableCell>
                        {activeType.person && <TableCell>{c.gender || "-"}</TableCell>}
                        {activeType.person && <TableCell className="whitespace-nowrap">{c.birth_date ? formatDate(c.birth_date) : "-"}</TableCell>}
                        <TableCell className="text-right">
                          {tab === "kartu_sehat" && user?.role === "admin" && (
                          <div className="flex justify-end gap-1">
                            <button
                              data-testid={`kartu-edit-${c.contact_id}`}
                              title="Isi No. RM & Masa Berlaku"
                              onClick={() => openKartuEdit(c)}
                              className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            {c.kartu_status !== "disetujui" ? (
                              <button
                                data-testid={`kartu-approve-${c.contact_id}`}
                                title="Kartu Disetujui — kirim kartu digital ke WA pemohon"
                                onClick={() => handleApproveKartu(c)}
                                className={`rounded-md p-1.5 transition-colors duration-200 ${
                                  confirmApprove === c.contact_id ? "bg-emerald-600 text-white" : "text-slate-400 hover:bg-emerald-50 hover:text-emerald-700"
                                }`}
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </button>
                            ) : (
                              c.card_url && (
                                <a
                                  data-testid={`kartu-view-${c.contact_id}`}
                                  href={c.card_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Lihat Kartu Digital"
                                  className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                                >
                                  <Eye className="h-4 w-4" />
                                </a>
                              )
                            )}
                          </div>
                          )}
                          {canEditData(user?.role) && !c.source?.startsWith("form_") && (
                          <div className="flex justify-end gap-1">
                            <button
                              data-testid={`contact-edit-${c.contact_id}`}
                              onClick={() => handleEdit(c)}
                              className="rounded-md p-1.5 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              data-testid={`contact-delete-${c.contact_id}`}
                              onClick={() => handleDelete(c)}
                              className={`rounded-md p-1.5 transition-colors duration-200 ${
                                confirmDelete === c.contact_id ? "bg-red-600 text-white" : "text-slate-400 hover:bg-red-50 hover:text-red-600"
                              }`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
        <Dialog open={!!kartuEdit} onOpenChange={(v) => !v && setKartuEdit(null)}>
          <DialogContent className="max-w-sm" data-testid="kartu-edit-dialog">
            <DialogHeader>
              <DialogTitle>Data Kartu Sehat — {kartuEdit?.name}</DialogTitle>
            </DialogHeader>
            {kartuEdit && (
            <form onSubmit={submitKartuEdit} className="space-y-4" data-testid="kartu-edit-form">
              <div className="space-y-2">
                <Label>No. Rekam Medis (RM)</Label>
                <Input data-testid="kartu-rm-input" placeholder="diisi oleh petugas" value={kartuForm.rm} onChange={(e) => setKartuForm({ ...kartuForm, rm: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Masa Berlaku Kartu s.d.</Label>
                <Input type="date" data-testid="kartu-valid-until-input" value={kartuForm.valid_until} onChange={(e) => setKartuForm({ ...kartuForm, valid_until: e.target.value })} />
              </div>
              <Button type="submit" data-testid="kartu-edit-save" disabled={kartuSaving} className="w-full bg-emerald-800 hover:bg-emerald-700 text-white">
                {kartuSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
